import { Fragment, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { fine, money } from '../../utils/format';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { DateInput } from '../../components/ui/Form';

interface Line {
  code: string;
  name: string;
  amount: string;
}

interface PlLine extends Line {
  gross: string;
  returns: string;
  group?: string;
}

interface MetalResult {
  metalId: string;
  metalCode: string;
  metalName: string;
  invoices: number;
  fineSold: string;
  sales: string;
  returns: string;
  netSales: string;
  costOfSales: string;
  grossProfit: string;
  marginPct: string;
  meltingLoss: string;
  profitAfterLoss: string;
}

interface ProfitAndLoss {
  revenue: PlLine[];
  grossSales: string;
  salesReturns: string;
  totalRevenue: string;
  cogs: PlLine[];
  totalCogs: string;
  grossProfit: string;
  grossMarginPct: string;
  otherIncome: PlLine[];
  totalOtherIncome: string;
  expenses: PlLine[];
  totalExpenses: string;
  netProfit: string;
  netMarginPct: string;
  byMetal: MetalResult[];
  tieOut: {
    invoiceSales: string;
    ledgerSales: string;
    otherSales: string;
    invoiceCost: string;
    ledgerCost: string;
    otherCost: string;
  };
  stockCheck: { agrees: boolean; difference: string; accounts: { code: string; name: string; difference: string }[] };
}

interface BalanceSheet {
  assets: Line[];
  totalAssets: string;
  liabilities: Line[];
  totalLiabilities: string;
  equity: Line[];
  totalEquity: string;
  currentYearProfit: string;
  totalEquityAndLiabilities: string;
  balanced: boolean;
  difference: string;
}

interface CashFlow {
  operating: string;
  investing: string;
  financing: string;
  netChange: string;
  closingCash: string;
}

interface TrialBalance {
  rows: {
    code: string;
    name: string;
    nature: string;
    closing_debit: string;
    closing_credit: string;
  }[];
  totalDebit: string;
  totalCredit: string;
  balanced: boolean;
}

type Tab = 'pl' | 'bs' | 'cf' | 'tb';

/** The Indian financial year that contains today: 1 April to 31 March. */
function currentFinancialYear(): { from: string; to: string } {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` };
}

/**
 * The four statements an owner, an auditor and the bank all ask for.
 *
 * They were always being computed — every posting goes through the journal,
 * so the figures exist the moment a sale is made — but no screen showed them,
 * which left a shop reading its profit off a dashboard card. Each one here
 * comes straight from the ledger, so they agree with each other by
 * construction: the P&L's net profit is the balance sheet's current-year
 * profit, and the balance sheet says so when it balances.
 */
export function StatementsPage() {
  const fy = currentFinancialYear();
  const [tab, setTab] = useState<Tab>('pl');
  const [from, setFrom] = useState(fy.from);
  const [to, setTo] = useState(fy.to);
  const { header } = usePrintHeader();

  const period = `from=${from}&to=${to}`;

  const pl = useQuery({
    queryKey: ['stmt-pl', from, to],
    queryFn: () => api.get<ProfitAndLoss>(`/reports/profit-and-loss?${period}`),
    enabled: tab === 'pl',
  });
  const bs = useQuery({
    queryKey: ['stmt-bs', to],
    queryFn: () => api.get<BalanceSheet>(`/reports/balance-sheet?asOf=${to}`),
    enabled: tab === 'bs',
  });
  const cf = useQuery({
    queryKey: ['stmt-cf', from, to],
    queryFn: () => api.get<CashFlow>(`/reports/cash-flow?${period}`),
    enabled: tab === 'cf',
  });
  const tb = useQuery({
    queryKey: ['stmt-tb', from, to],
    queryFn: () => api.get<TrialBalance>(`/reports/trial-balance?${period}`),
    enabled: tab === 'tb',
  });

  const active = { pl, bs, cf, tb }[tab];

  const tabs: { key: Tab; label: string }[] = [
    { key: 'pl', label: 'Profit & Loss' },
    { key: 'bs', label: 'Balance Sheet' },
    { key: 'cf', label: 'Cash Flow' },
    { key: 'tb', label: 'Trial Balance' },
  ];

  const titles: Record<Tab, string> = {
    pl: 'Profit & Loss Account',
    bs: 'Balance Sheet',
    cf: 'Cash Flow Statement',
    tb: 'Trial Balance',
  };

  return (
    <>
      <PageHeader
        title="Financial statements"
        subtitle="Straight from the ledger — the same figures an auditor or a bank would ask for."
        actions={
          <button className="btn-ghost no-print" onClick={() => window.print()}>
            Print
          </button>
        }
      />

      <div className="no-print mb-4 flex flex-wrap items-end gap-3">
        <div className="flex overflow-hidden rounded-md border border-velvet">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`px-4 py-1.5 text-sm font-medium ${
                tab === t.key ? 'bg-velvet text-white' : 'bg-surface text-ink-soft hover:bg-gold-50'
              }`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab !== 'bs' ? (
          <div>
            <p className="label">From</p>
            <DateInput value={from} onChange={setFrom} allowFuture />
          </div>
        ) : null}
        <div>
          <p className="label">{tab === 'bs' ? 'As at' : 'To'}</p>
          <DateInput value={to} onChange={setTo} allowFuture />
        </div>
        <button
          className="btn-quiet text-2xs"
          onClick={() => {
            setFrom(fy.from);
            setTo(fy.to);
          }}
        >
          This financial year
        </button>
      </div>

      <ErrorNotice error={active.error} />
      {active.isLoading ? <Loading rows={6} /> : null}

      <div className="print-sheet overflow-x-auto rounded-xl border border-silver-100 bg-surface p-3 shadow-card sm:p-6">
        <div className="mb-4 border-b-2 border-gold-600 pb-2 text-center">
          <p className="text-base font-bold">{header.companyName}</p>
          <p className="text-sm font-semibold">{titles[tab]}</p>
          <p className="num text-2xs text-ink-soft">{tab === 'bs' ? `As at ${to}` : `${from} to ${to}`}</p>
        </div>

        {tab === 'pl' && pl.data ? <ProfitLoss d={pl.data} /> : null}
        {tab === 'bs' && bs.data ? <Balance d={bs.data} /> : null}
        {tab === 'cf' && cf.data ? <Cash d={cf.data} /> : null}
        {tab === 'tb' && tb.data ? <Trial d={tb.data} /> : null}
      </div>
    </>
  );
}

/* ─────────────────────────── building blocks ─────────────────────────── */

function Section({
  title,
  lines,
  total,
  totalLabel,
}: {
  title: string;
  lines: Line[];
  total: string;
  totalLabel: string;
}) {
  return (
    <div className="mb-4">
      <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-soft">{title}</p>
      {lines.length === 0 ? (
        <p className="py-1 text-sm text-ink-faint">None in this period</p>
      ) : (
        lines.map((l) => (
          <div key={l.code} className="flex justify-between border-b border-silver-100/60 py-1 text-sm">
            <span>
              <span className="num mr-2 text-2xs text-ink-faint">{l.code}</span>
              {l.name}
            </span>
            <span className="num">{money(l.amount)}</span>
          </div>
        ))
      )}
      <div className="flex justify-between py-1 text-sm font-semibold">
        <span>{totalLabel}</span>
        <span className="num">{money(total)}</span>
      </div>
    </div>
  );
}

function Result({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  const negative = Number(value) < 0;
  return (
    <div className="my-3 flex items-baseline justify-between rounded-md bg-gold-50 px-3 py-2">
      <span className="font-semibold">
        {label}
        {hint ? <span className="ml-2 text-2xs font-normal text-ink-soft">{hint}</span> : null}
      </span>
      <span className={`num text-lg font-bold ${negative ? 'text-danger' : 'text-success'}`}>
        {negative ? '(' : ''}
        {money(String(Math.abs(Number(value))))}
        {negative ? ')' : ''}
      </span>
    </div>
  );
}

/** An amount as an accountant writes it: a negative in brackets. */
function Amt({ v, strong }: { v: string; strong?: boolean }) {
  const x = Number(v);
  return (
    <span className={`num ${strong ? 'font-semibold' : ''} ${x < 0 ? 'text-danger' : ''}`}>
      {x < 0 ? `(${money(String(Math.abs(x)))})` : money(v)}
    </span>
  );
}

function PlRow({
  code,
  name,
  cells,
  strong,
  indent,
}: {
  code?: string;
  name: ReactNode;
  cells: (string | null)[];
  strong?: boolean;
  indent?: boolean;
}) {
  return (
    <tr className={`border-b border-silver-100/60 ${strong ? 'font-semibold' : ''}`}>
      <td className={`py-1 pr-2 text-sm ${indent ? 'pl-4' : ''}`}>
        {code ? <span className="num mr-2 text-2xs text-ink-faint">{code}</span> : null}
        {name}
      </td>
      {cells.map((c, i) => (
        <td key={i} className="w-32 py-1 text-right text-sm">
          {c === null ? '' : <Amt v={c} strong={strong} />}
        </td>
      ))}
    </tr>
  );
}

function PlHead({ title, cols }: { title: string; cols: string[] }) {
  return (
    <tr>
      <td className="pb-1 pt-4 text-2xs font-semibold uppercase tracking-wide text-ink-soft">{title}</td>
      {cols.map((c) => (
        <td key={c} className="pb-1 pt-4 text-right text-2xs font-semibold uppercase tracking-wide text-ink-soft">
          {c}
        </td>
      ))}
    </tr>
  );
}

function ProfitLoss({ d }: { d: ProfitAndLoss }) {
  // Expenses under their group heads, as an accountant lays them out.
  const groups = new Map<string, PlLine[]>();
  for (const l of d.expenses) {
    const g = l.group ?? 'Expenses';
    groups.set(g, [...(groups.get(g) ?? []), l]);
  }
  const anyReturns = Number(d.salesReturns) !== 0;
  const blank = anyReturns ? [null, null] : [];

  return (
    <div className="mx-auto max-w-4xl">
      {!d.stockCheck.agrees ? (
        <div className="no-print mb-4 rounded-md border border-danger/40 bg-danger/5 p-3 text-sm">
          <span className="font-semibold text-danger">The stock accounts do not agree with the stock on hand</span> — by{' '}
          <span className="num">{money(d.stockCheck.difference)}</span>, so this profit is off by up to that much.{' '}
          <a className="text-gold-700 underline" href="/melting/position">
            See where, and bring the books into line
          </a>
          .
        </div>
      ) : null}

      <table className="w-full">
        <tbody>
          <PlHead title="Sales" cols={anyReturns ? ['Sales', 'Less returns', 'Net'] : ['Amount']} />
          {d.revenue.map((l) => (
            <PlRow
              key={l.code}
              code={l.code}
              name={l.name}
              cells={anyReturns ? [l.gross, l.returns, l.amount] : [l.amount]}
              indent
            />
          ))}
          <PlRow
            name="Net sales"
            cells={anyReturns ? [d.grossSales, d.salesReturns, d.totalRevenue] : [d.totalRevenue]}
            strong
          />

          <PlHead title="Less: cost of sales" cols={[]} />
          {d.cogs.map((l) => (
            <PlRow key={l.code} code={l.code} name={l.name} cells={[...blank, l.amount]} indent />
          ))}
          <PlRow name="Total cost of sales" cells={[...blank, d.totalCogs]} strong />
        </tbody>
      </table>
      <Result label="Gross profit" value={d.grossProfit} hint={`${d.grossMarginPct}% of net sales`} />

      <table className="w-full">
        <tbody>
          {d.otherIncome.length > 0 ? (
            <>
              <PlHead title="Add: other income" cols={[]} />
              {d.otherIncome.map((l) => (
                <PlRow key={l.code} code={l.code} name={l.name} cells={[...blank, l.amount]} indent />
              ))}
              <PlRow name="Total other income" cells={[...blank, d.totalOtherIncome]} strong />
            </>
          ) : null}

          <PlHead title="Less: expenses" cols={[]} />
          {[...groups].map(([g, lines]) => (
            <Fragment key={g}>
              {groups.size > 1 ? (
                <PlRow name={<span className="text-ink-soft">{g}</span>} cells={[...blank, null]} />
              ) : null}
              {lines.map((l) => (
                <PlRow key={l.code} code={l.code} name={l.name} cells={[...blank, l.amount]} indent />
              ))}
            </Fragment>
          ))}
          {d.expenses.length === 0 ? (
            <PlRow name={<span className="text-ink-faint">None in this period</span>} cells={[...blank, null]} />
          ) : null}
          <PlRow name="Total expenses" cells={[...blank, d.totalExpenses]} strong />
        </tbody>
      </table>
      <Result
        label={Number(d.netProfit) < 0 ? 'Net loss' : 'Net profit'}
        value={d.netProfit}
        hint={`${d.netMarginPct}% of net sales · gross profit ${money(d.grossProfit)} + other income ${money(d.totalOtherIncome)} − expenses ${money(d.totalExpenses)}`}
      />

      {d.byMetal.length > 0 ? (
        <div className="mt-6">
          <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-soft">
            Trading by metal — from the invoices and credit notes
          </p>
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b border-ink/20 text-2xs uppercase tracking-wide text-ink-soft">
                  <th className="py-1 pr-3 text-left">Metal</th>
                  <th className="py-1 text-right">Fine sold</th>
                  <th className="py-1 text-right">Net sales</th>
                  <th className="py-1 text-right">Cost</th>
                  <th className="py-1 text-right">Gross profit</th>
                  <th className="py-1 text-right">Margin</th>
                  <th className="py-1 text-right">Metal lost</th>
                  <th className="py-1 text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {d.byMetal.map((m) => (
                  <tr key={m.metalId} className="border-b border-silver-100/60 [&>td]:px-1.5">
                    <td className="py-1 pr-3 font-medium">
                      {m.metalName}
                      <span className="block text-2xs font-normal text-ink-faint">
                        {m.invoices} bill{m.invoices === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td className="num py-1 text-right">{fine(m.fineSold)}</td>
                    <td className="py-1 text-right">
                      <Amt v={m.netSales} />
                      {Number(m.returns) > 0 ? (
                        <span className="block text-2xs text-ink-faint">after {money(m.returns)} returns</span>
                      ) : null}
                    </td>
                    <td className="py-1 text-right">
                      <Amt v={m.costOfSales} />
                    </td>
                    <td className="py-1 text-right">
                      <Amt v={m.grossProfit} strong />
                    </td>
                    <td className="num py-1 text-right">{m.marginPct}%</td>
                    <td className="py-1 text-right">{Number(m.meltingLoss) !== 0 ? <Amt v={m.meltingLoss} /> : '—'}</td>
                    <td className="py-1 text-right">
                      <Amt v={m.profitAfterLoss} strong />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-2xs text-ink-soft">
            Net sales {money(d.tieOut.ledgerSales)}: {money(d.tieOut.invoiceSales)} from invoices
            {Number(d.tieOut.otherSales) !== 0
              ? `, ${money(d.tieOut.otherSales)} entered directly in the books (scrap sold, manual entries)`
              : ''}
            . Cost of sales {money(d.tieOut.ledgerCost)}: {money(d.tieOut.invoiceCost)} from invoices and metal lost in
            melting and job work
            {Number(d.tieOut.otherCost) !== 0
              ? `, ${money(d.tieOut.otherCost)} from other entries (job-work charges, manual entries)`
              : ''}
            . “Metal lost” is metal written off: melting loss above the allowance (loss within it is already in the
            bars’ cost), and metal that did not come back from job work.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Balance({ d }: { d: BalanceSheet }) {
  return (
    <>
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <Section title="Assets" lines={d.assets} total={d.totalAssets} totalLabel="Total assets" />
        </div>
        <div>
          <Section
            title="Liabilities"
            lines={d.liabilities}
            total={d.totalLiabilities}
            totalLabel="Total liabilities"
          />
          <Section title="Capital" lines={d.equity} total={d.totalEquity} totalLabel="Total capital" />
          <div className="flex justify-between border-b border-silver-100/60 py-1 text-sm">
            <span>Profit for the year so far</span>
            <span className={`num ${Number(d.currentYearProfit) < 0 ? 'text-danger' : ''}`}>
              {money(d.currentYearProfit)}
            </span>
          </div>
          <div className="flex justify-between py-1 text-sm font-semibold">
            <span>Total liabilities and capital</span>
            <span className="num">{money(d.totalEquityAndLiabilities)}</span>
          </div>
        </div>
      </div>
      {/* An owner should never have to check this by hand. */}
      <p className={`mt-4 text-center text-sm ${d.balanced ? 'text-success' : 'font-semibold text-danger'}`}>
        {d.balanced
          ? `Balanced — assets of ${money(d.totalAssets)} equal liabilities and capital.`
          : `Does not balance — off by ${money(d.difference)}. Run the integrity checks.`}
      </p>
    </>
  );
}

function Cash({ d }: { d: CashFlow }) {
  const row = (label: string, v: string, strong = false) => (
    <div className={`flex justify-between border-b border-silver-100/60 py-2 text-sm ${strong ? 'font-semibold' : ''}`}>
      <span>{label}</span>
      <span className={`num ${Number(v) < 0 ? 'text-danger' : ''}`}>{money(v)}</span>
    </div>
  );
  return (
    <div className="mx-auto max-w-xl">
      {row('Cash from trading (operating)', d.operating)}
      {row('Cash from investment (investing)', d.investing)}
      {row('Cash from loans and capital (financing)', d.financing)}
      <Result label="Net change in cash" value={d.netChange} />
      {row('Cash and bank at the end of the period', d.closingCash, true)}
      <p className="mt-3 text-2xs text-ink-soft">
        A shop that bought heavily for stock will show negative operating cash even while trading profitably — the money
        is sitting on the shelf, not lost.
      </p>
    </div>
  );
}

function Trial({ d }: { d: TrialBalance }) {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="th">Code</th>
              <th className="th">Account</th>
              <th className="th text-right">Debit</th>
              <th className="th text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {d.rows
              .filter((r) => Number(r.closing_debit) !== 0 || Number(r.closing_credit) !== 0)
              .map((r) => (
                <tr key={r.code}>
                  <td className="td num text-2xs text-ink-soft">{r.code}</td>
                  <td className="td">{r.name}</td>
                  <td className="td num text-right">{Number(r.closing_debit) ? money(r.closing_debit) : ''}</td>
                  <td className="td num text-right">{Number(r.closing_credit) ? money(r.closing_credit) : ''}</td>
                </tr>
              ))}
            <tr className="bg-gold-50 font-semibold">
              <td className="td" colSpan={2}>
                Total
              </td>
              <td className="td num text-right">{money(d.totalDebit)}</td>
              <td className="td num text-right">{money(d.totalCredit)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className={`mt-3 text-center text-sm ${d.balanced ? 'text-success' : 'font-semibold text-danger'}`}>
        {d.balanced ? 'Debits equal credits.' : 'Debits and credits do not agree — run the integrity checks.'}
      </p>
    </>
  );
}
